import React, { useMemo } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { MatchProfile } from '../components/match/MatchProfile';
import { MatchSchedule } from '../components/match/MatchSchedule';
import { MatchConfirmation } from '../components/match/MatchConfirmation';
import { useMatch } from '../state/MatchContext';
import { MatchFallback } from '../components/match/MatchFallback';

const DEFAULT_GUIDE_ID = 'current';

export function SeekerOnboardingMatchRoutes() {
    const { matchResult } = useMatch();

    const defaultGuideId = useMemo(() => {
        if (matchResult?.status === 'matched' && matchResult.guide?.id) {
            return matchResult.guide.id;
        }
        return DEFAULT_GUIDE_ID;
    }, [matchResult?.status, matchResult?.guide?.id]);

    return (
        <Routes>
            <Route index element={<Navigate to={`profile/${defaultGuideId}`} replace />} />
            <Route path="profile/:guideId" element={<MatchProfilePage />} />
            <Route path="schedule/:guideId" element={<MatchSchedulePage />} />
            <Route path="booking/confirmed" element={<MatchConfirmationPage />} />
            <Route path="*" element={<Navigate to={`profile/${defaultGuideId}`} replace />} />
        </Routes>
    );
}

function MatchProfilePage() {
    const { guideId } = useParams<{ guideId: string }>();
    const navigate = useNavigate();
    const { matchResult } = useMatch();

    const guide = matchResult?.status === 'matched' ? matchResult.guide : undefined;

    if (!guide) {
        return (
            <MatchFallback
                title="Guide profile coming soon"
                message="We’ll show your matched guide’s details here as soon as the matching process is complete. Please check back shortly."
                onBack={() => navigate(-1)}
            />
        );
    }

    const profileGuideId = guide.id ?? guideId ?? DEFAULT_GUIDE_ID;

    return (
        <MatchProfile
            guide={guide}
            onBack={() => navigate(-1)}
            onHelp={() => undefined}
            onViewSchedule={() => navigate(`/onboarding/match/schedule/${profileGuideId}`)}
        />
    );
}

function MatchSchedulePage() {
    const { guideId } = useParams<{ guideId: string }>();
    const navigate = useNavigate();
    const { matchResult } = useMatch();

    const hasMatch = matchResult?.status === 'matched';
    const sessions = matchResult?.sessions ?? [
        {
            id: 'session-1',
            title: 'Consultation Session',
            guideName: matchResult?.guide?.fullName ?? 'Sarah',
            timeRange: '2:00 PM - 3:00 PM',
            mode: 'Video Call'
        },
        {
            id: 'session-2',
            title: 'Consultation Session',
            guideName: matchResult?.guide?.fullName ?? 'Sarah',
            timeRange: '4:00 PM - 5:00 PM',
            mode: 'Video Call'
        }
    ];

    if (!hasMatch) {
        return (
            <MatchFallback
                title="Schedule coming soon"
                message="We’ll unlock booking times right after we’ve matched you with the best guide for your journey."
                onBack={() => navigate(-1)}
            />
        );
    }

    const scheduleGuideId = guideId ?? matchResult.guide?.id ?? DEFAULT_GUIDE_ID;

    return (
        <MatchSchedule
            sessions={sessions}
            onBack={() => navigate(-1)}
            onHelp={() => undefined}
            onBookSession={sessionId => navigate(`/onboarding/match/booking/confirmed?session=${sessionId}&guide=${scheduleGuideId}`)}
        />
    );
}

function MatchConfirmationPage() {
    const navigate = useNavigate();

    return <MatchConfirmation onBack={() => navigate(-1)} onHelp={() => undefined} />;
}

type MatchFallbackProps = {
    title: string;
    message: string;
    onBack: () => void;
};


